import React from "react";
import styled, { useTheme } from "styled-components";
import {
  LanguageNameSpaces,
  PaymentDetails,
  SubscriptionType,
} from "@shared/types";
import { formatDate, sortRawDates } from "@shared/utils/date";
import usePayments from "@shared/features/settings/subscription/hooks/usePayments";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Panel from "common/NewLook/Panel.tsx";
import Skeleton from "common/NewLook/Skeleton.tsx";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import useMedia from "utils/hooks/useMedia";
import { NEW_LOOK_CARDS_VIEW_MAX_WIDTH } from "common/NewLook/layout.ts";
import PaymentsTable from "features/settings/Billing/PaymentsTable";
import PaymentStatusTag from "features/settings/Billing/PaymentStatusTag";
import PanelEmpty from "common/Empty/PanelEmpty.tsx";

const SKELETON_ROWS = [220, 180, 240, 200];

interface NewLookBillingHistoryProps {
  subscriptionType: SubscriptionType;
}

/**
 * Billing history in the new look: the payments table on wide screens and a
 * stacked list of payments on phones, with a compact empty state.
 */
const NewLookBillingHistory = ({
  subscriptionType,
}: NewLookBillingHistoryProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const isStacked = useMedia(
    [`(max-width: ${NEW_LOOK_CARDS_VIEW_MAX_WIDTH})`],
    [true],
    false
  );
  const { isFetching, paymentsData, getPaymentDate } = usePayments({
    subscriptionType,
  });
  const isGroupPayments = subscriptionType === SubscriptionType.GROUP;
  const empty = (
    <PanelEmpty
      title={t("billing_history_empty.title")}
      description={t("billing_history_empty.description")}
    />
  );

  const getContent = () => {
    // First load: rows in outline instead of a spinner over an empty table.
    if (isFetching && !paymentsData.length) {
      return (
        <List aria-busy={true}>
          {SKELETON_ROWS.map((width, index) => (
            <SkeletonRow key={index}>
              <Skeleton width={width} height={14} />
              <Skeleton width={64} height={14} />
            </SkeletonRow>
          ))}
        </List>
      );
    }
    if (!paymentsData.length) return empty;
    if (!isStacked) {
      return (
        <TableWrapper>
          <PaymentsTable
            subscriptionType={subscriptionType}
            emptyText={empty}
          />
        </TableWrapper>
      );
    }
    const sorted = paymentsData
      .slice()
      .sort((a, b) => sortRawDates(getPaymentDate(b), getPaymentDate(a)));
    return (
      <List>
        {sorted.map((payment: PaymentDetails & { key: number }) => {
          const name = isGroupPayments
            ? payment.group_name
            : payment.subscription_type;
          return (
            <Row key={payment.key}>
              <RowTop>
                <Name>
                  {[name, payment.duration].filter(Boolean).join(" · ")}
                </Name>
                <PaymentStatusTag paymentDetails={payment} />
              </RowTop>
              <RowBottom>
                <Meta>
                  {[
                    formatDate(getPaymentDate(payment), true, true),
                    payment.payment_option,
                    payment.action,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </Meta>
                {!!payment.cost && <Cost>${payment.cost} USD</Cost>}
              </RowBottom>
            </Row>
          );
        })}
      </List>
    );
  };

  return (
    <Panel title={t("billing_history_title")} flush>
      <LoadingSpinnerOverlay
        isLoading={isFetching && isStacked}
        backgroundColor={theme.colors.cardBgColor}
      >
        {getContent()}
      </LoadingSpinnerOverlay>
    </Panel>
  );
};

export default NewLookBillingHistory;

const TableWrapper = styled.div`
  .ant-table-content {
    overflow-x: auto;
  }
  /* Flush in the panel: the outer columns line up with the panel title,
     body rows are 56px (the rows carry a global !important padding). */
  && .ant-table-thead > tr > th,
  && .ant-table-tbody > tr > td {
    padding: 12px !important;
    white-space: nowrap;
  }
  && .ant-table-tbody > tr > td {
    padding-block: 16px !important;
    ${newLookType.body};
    font-variant-numeric: tabular-nums;
  }
  && .ant-table-thead > tr > th:first-child,
  && .ant-table-tbody > tr > td:first-child {
    padding-left: 20px !important;
  }
  && .ant-table-thead > tr > th:last-child,
  && .ant-table-tbody > tr > td:last-child {
    padding-right: 20px !important;
  }
  &&& .ant-table-tbody > tr > td.ant-table-column-sort {
    background: transparent;
  }
  .ant-table-pagination.ant-pagination {
    margin: 12px 20px;
  }
`;
const List = styled.div`
  display: flex;
  flex-direction: column;
`;
const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 20px;
  &:not(:last-child) {
    border-bottom: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
const RowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  a {
    flex: none;
  }
`;
const RowBottom = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
`;
const Name = styled.div`
  min-width: 0;
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Meta = styled.div`
  min-width: 0;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Cost = styled.div`
  flex: none;
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-height: 52px;
  padding: 0 20px;
  & + & {
    border-top: 1px solid ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
